import React from 'react';
import PropTypes from 'prop-types';
import loadScript from 'load-script';
import { toJS } from 'mobx';
import { observer } from 'mobx-react-lite';

import { Button, Dialog, DialogTitle, DialogContent, Menu,
         MenuItem, Typography, DialogActions, List, ListItem,
         ListItemIcon, ListItemText, TextField, Snackbar, } from '@material-ui/core';
import MuiAlert from '@material-ui/lab/Alert';

import { Folder, Description, ArrowUpward } from '@material-ui/icons';
import { ClaimSet } from './CGModel';

// Both scripts are now required: GAPI for Picker/Client, GIS for Authentication
const GAPI_SDK_URL = 'https://apis.google.com/js/api.js';
const GIS_SDK_URL = 'https://accounts.google.com/gsi/client';

const API_KEY = 'AIzaSyATi6_qe5ud1dqoxsZr0t8O_1HvuUeRMxA';
const CLIENT_ID = "367498997284-kegc41cifibcgjrnip2np3428s1ll6tr.apps.googleusercontent.com";
var appId = "367498997284";

// MODIFIED: Added userinfo profile scope
var scope = 'https://www.googleapis.com/auth/drive.file https://www.googleapis.com/auth/userinfo.profile';

let gapiScriptLoaded = false;
let gisScriptLoaded = false;
let tokenClient = null;

export var OAuthToken = null;
var pickerApiLoaded = false;
export var userProfile = null; 

let authCallbackQueue = null;

function isGoogleReady() {
  return !!window.gapi && !!window.google?.accounts?.oauth2;
}

export function signOut(workshop) {
  console.log('Disconnecting');
  if (OAuthToken && window.google?.accounts?.oauth2) {
    window.google.accounts.oauth2.revoke(OAuthToken, () => {
      console.log('Token revoked');
    });
  }
  userProfile = null;
  OAuthToken = null;
  if (workshop) workshop.setGoogleUser(null);
}

function initAuth() {
  if (!gapiScriptLoaded) {
    gapiScriptLoaded = true;
    loadScript(GAPI_SDK_URL, loadGapiAPI);
  }
  if (!gisScriptLoaded) {
    gisScriptLoaded = true;
    loadScript(GIS_SDK_URL, initTokenClient);
  }
}

function loadGapiAPI(err) {
  if (err) {
    console.error("Error loading GAPI script:", err);
    return;
  }
  console.log('GAPI script loaded');
  window.gapi.load('client', () => {
    window.gapi.client.init({
      apiKey: API_KEY,
    }).then(() => {
      console.log('GAPI client initialized');
    });
  });
}

function initTokenClient(err) {
  if (err) {
    console.error("Error loading GIS script:", err);
    return;
  }
  console.log('GIS script loaded');
  
  // Initialize the modern token client
  tokenClient = window.google.accounts.oauth2.initTokenClient({
    client_id: CLIENT_ID,
    scope: scope,
    callback: async (tokenResponse) => {
      if (tokenResponse && tokenResponse.access_token) {
        console.log('Authenticated successfully');
        OAuthToken = tokenResponse.access_token;
        
        // MODIFIED: Fetch user profile data using the access token
        try {
          const res = await fetch('https://www.googleapis.com/oauth2/v3/userinfo', {
            headers: { Authorization: `Bearer ${OAuthToken}` },
          });
          userProfile = await res.json();
          console.log('User profile retrieved:', userProfile);
        } catch (fetchErr) {
          console.error("Failed to fetch user profile:", fetchErr);
        }

        // Execute the deferred callback if one exists
        if (authCallbackQueue) {
          authCallbackQueue();
          authCallbackQueue = null;
        }
      } else {
        console.error("Authentication failed or cancelled");
      }
    },
  });
}

export function authenticated(callback) {
  console.log('Authenticated call');
  
  if (!isGoogleReady() || !tokenClient) {
    console.warn('Google scripts are still loading...');
    return;
  }

  // If we already have a token, fire the callback immediately
  if (OAuthToken) {
    callback();
  } else {
    // Queue the callback to be fired after token resolution
    authCallbackQueue = callback;
    
    // Request a token (triggers the modern Google popup login)
    tokenClient.requestAccessToken({ prompt: '' }); // was 'consent'
  }
}

// Start loading the identity and client infrastructure
initAuth();

export function listFiles(folder, callback) {
  console.log('Listing', folder); 
  
  const _list = () => {
    var url = `https://www.googleapis.com/drive/v3/files?fields=files(name,id,modifiedTime,mimeType,parents)&key=${API_KEY}&`;
    url += `q='${folder}' in parents and trashed=false and (name contains '.cws' or mimeType='application/vnd.google-apps.folder')`;
    
    var accessToken = OAuthToken; 
    
    fetch(url, {
      method: 'GET',
      headers: new Headers({ 'Authorization': 'Bearer ' + accessToken }),
    }).then((res) => {
      console.log('RESULT', res);
      return res.json();
    }).then(function(val) {
      console.log('FETCH', val);
      if (callback) callback(val.files);
    }).catch(function(err) {
      console.log('ERROR', err);
    });
  };
  authenticated(_list);
}

// MODIFIED: Added full 'drive' scope if accessing pre-existing Drive files
//var scope = 'https://www.googleapis.com/auth/drive https://www.googleapis.com/auth/userinfo.profile';

export function saveToGDrive(workshop, doc, callback) {
  const _save = () => {
    console.log('SAVING', toJS(workshop), doc);
    var file = new Blob([JSON.stringify(workshop.claimSet.dumpClaims(), null, 2)], { type: 'application/json' });
    
    var method = 'POST';
    var url = `https://www.googleapis.com/upload/drive/v3/files?uploadType=multipart`;
    
    // Base metadata for new file
    var metadata = { 
      name: doc.name, 
      mimeType: 'application/json' 
    };

    if (doc.parentId) {
      metadata.parents = [doc.parentId];
    }

    // FIX: If updating an existing file, use PATCH without 'parents' or 'id' in body
    if (doc.id) {
      method = 'PATCH';
      url = `https://www.googleapis.com/upload/drive/v3/files/${doc.id}?uploadType=multipart`;
      // Clean metadata payload for PATCH (do not send parents or id in body)
      metadata = {
        name: doc.name,
        mimeType: 'application/json'
      };
    }

    var accessToken = OAuthToken; 
    console.log(metadata, accessToken);

    var form = new FormData();
    form.append('metadata', new Blob([JSON.stringify(metadata)], { type: 'application/json' }));
    form.append('file', file);

    fetch(url, { 
      method: method,
      headers: new Headers({ 'Authorization': 'Bearer ' + accessToken }),
      body: form,
    }).then((res) => {
      console.log('RESULT', res);
      if (!res.ok) {
        return res.json().then(err => Promise.reject(err));
      }
      return res.json();
    }).then(function(val) {
      console.log('SAVE_FETCH', val);
      callback(val);
    }).catch(function(err) {
      console.error('ERROR SAVING TO GDRIVE:', err);
    });
  };

  authenticated(_save);
}

function parseResponse(text) {
  if (text.startsWith('--')) {
    var start = text.indexOf('{');
    var end = text.indexOf('}', start+1);
    const doc = JSON.parse(text.slice(start, end+1));
    start = text.indexOf('{', end);
    end = text.lastIndexOf('\n');
    end = text.lastIndexOf('\n', end-1);
    const contents = JSON.parse(text.slice(start, end));
    return {doc, contents};
  }
  return {contents: JSON.parse(text)};
}

export function loadFromGDrive(workshop, callback) {
  const _load = () => {
    console.log('LOADING', workshop.doc);
    var method = 'GET';
    var url = `https://www.googleapis.com/drive/v3/files/${workshop.doc.id}?alt=media`;
    
    var accessToken = OAuthToken; 
    console.log(url);
    console.log(accessToken);
    
    fetch(url, { 
      method: method,
      headers: new Headers({ 'Authorization': 'Bearer ' + accessToken }),
    }).then((res) => {
      console.log('RESULT', res);
      return res.blob();
    }).then(function(blob) {
      return blob.text();
    }).then(function(text) {
      console.log('FETCH', text);
      const resp = parseResponse(text);
      console.log('FETCHR', resp);
      workshop.setClaimSet(ClaimSet.claimSetFromJSON(resp.contents));
      callback(resp.doc || workshop.doc);
    }).catch(function(err) {
      console.log('ERROR', err);
    });
    
  };
  authenticated(_load);
}

// -----------------------------------------------------------------------
// UI Elements
// -----------------------------------------------------------------------

const Notice = ({open}) => {
  const [isOpen, setOpen] = React.useState(open);
  
  return (
    <Dialog open={isOpen} onClose={() => setOpen(false)} scroll='body'>
      <div style={{padding: '1.5em'}}>
        <Typography variant="h5" paragraph={true}>
          Use Google Drive?
        </Typography>
        <Typography variant="body1" display='block' paragraph={true}>
          ClaimWorkShop would like to access your personal
          Google Drive account to store your files.  Why?
        </Typography>
        <Typography variant="body1" display='block' paragraph={false}>
          The content of your claim file is your confidential information.
          You keep control of your claim files by storing them on your Google Drive.
          If you grant access, ClaimWorkShop will only be able to
          access the files that it has created, along with your public profile
          information, such as your name and email address.
        </Typography>
        <DialogActions>
          <Button
            onClick={() => {
              setOpen(false);
            }}
            color="primary" autoFocus >
            Got It
          </Button>
        </DialogActions>
      </div>
    </Dialog>
  );
};


export const FileMenu = observer((props) => {
  const workshop = props.workshop;
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [giveNotice, setGiveNotice] = React.useState(null);
  const [showError, setShowError] = React.useState(null);
  //const [doc, setDoc] = React.useState({name: '', id: null});
  const [path, setPath] = React.useState([{id: 'root', name: 'GDrive'}]);
  const [docs, setDocs] = React.useState([]);
  const [fetching, setFetching] = React.useState(false);
  const [pickerVisible, setPickerVisible] = React.useState(false);
  const [action, setAction] = React.useState('save');
  const [warningVisible, setWarningVisible] = React.useState(false);
  
  const list = (fileId) => {
    console.log('Listing');
    setFetching(true);
    listFiles(fileId, (files) => {
      console.log('Setting docs');
      setDocs(files || []);
      workshop.setGoogleUser(userProfile);
      setFetching(false);
    });
  };
  
  const save = () => {
    setFetching(true);
    console.log('SAVING', action, workshop.doc);
    saveToGDrive(workshop, workshop.doc, (doc) => {
      workshop.setGoogleUser(userProfile);
      workshop.claimSet.setDirty(false);
      setFetching(false);
    });
  };
  
  const load = () => {
    setFetching(true);
    loadFromGDrive(workshop, (doc) => {
      workshop.setDoc(doc);
      workshop.setGoogleUser(userProfile);
      workshop.claimSet.setDirty(false);
      setFetching(false);
    });
  };

  // Makes sure that unsaved file is saved first
  const newWithWarning = () => {
    if (workshop.claimSet.dirty) {
      setAction('new');
      setWarningVisible(true);
    }
    else {
      close();
      workshop.newClaimSet();
    }
  };


  // If file selected just do it, otherwise pick
  const doSave = () => {
    if (giveNotice === null)
      setGiveNotice(true);
    else if (workshop.doc.id) {
      close();
      save();
    }
    else {
      openPicker('save');
    }
  };
  
  // Makes sure that unsaved file is saved first
  const loadWithWarning = () => {
    if (giveNotice === null) {
      setGiveNotice(true);
    }
    else if (workshop.claimSet.dirty)  {
      console.log('warning');
      setAction('load');
      setWarningVisible(true);
    }
    else {
      console.log('picking');
      openPicker('load');
    }
  };

  const close = () => {
    setAnchorEl(null);
    setPickerVisible(false);
    setWarningVisible(false);
  };

  const openPicker = (action) => {
    setAction(action);
    //if (action == 'save as')
    //  setDoc({name : '', id : null});
    list(path[0].id);
    setPickerVisible(true);
  };

  return (
    <span>
      {giveNotice && <Notice open={giveNotice}/>}
      {showError && <Snackbar open={showError}>
                     <MuiAlert severity="error" onClose={() => setShowError(null)}>
                       {showError}
                       </MuiAlert>
                    </Snackbar>}
      <Button variant="text"
              onClick={(e) => {
                setAnchorEl(e.currentTarget);
              }}>
        File
      </Button>
      <Menu anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            keepMounted
            onClose={close}
      >
        <div style={{fontWeight: 500, margin: '0.5em 0.5em 0em'}} key='filemenu_sub1'>
          GOOGLE DRIVE
        </div>
        <MenuItem disabled={fetching} onClick={doSave} >
          Save
        </MenuItem>
        <MenuItem disabled={fetching} onClick={() => openPicker('save as') }>
          Save As
        </MenuItem>
        <MenuItem disabled={fetching} onClick={loadWithWarning}>
          Load
        </MenuItem>
        <div style={{fontWeight: 500, margin: '0.5em 0.5em 0em'}} key='filemenu_sub2'>
          LOCAL
        </div>
        <MenuItem
          key='filemenu-upload'
          onClick={()=>{
            const input = document.createElement('input');
            input.type = 'file';
            input.accept = '.cws';
            input.onchange = (e) => {
              console.log('ONCHANGE');
              const file = e.target.files[0];
              window.theFile = file;
              file.text()
                .then(text => {
                  const json = JSON.parse(text);
                  console.log('Upload received', json);
                  workshop.setClaimSet(ClaimSet.claimSetFromJSON(json));
                })
                .catch(error => {
                  console.log('Upload error', error);
                  setShowError('Error, does not look like a ClaimWorkShop file');
                });
            };
            input.click();
            close();
          }}>
          Upload
        </MenuItem>
        <MenuItem
          key='filemenu_download'
          onClick={()=>{
            const blob = new Blob([JSON.stringify(workshop.claimSet.dumpClaims(), null, 2)],
                                  {type: 'application/json;charset=utf8;'});
            const element = document.createElement('a');
            element.setAttribute("href", window.URL.createObjectURL(blob));
            element.setAttribute("download", 'claimfile.cws');
            element.style.display = "";
            element.click();
            close();
          }}
        >
          Download
        </MenuItem>
        <MenuItem
          onClick={()=>{
            const blob = new Blob([workshop.toHTML()],
                                  {type: 'text/html;charset=utf8;'});
            const element = document.createElement('a');
            element.setAttribute("href", window.URL.createObjectURL(blob));
            element.setAttribute("download", 'claimfile.html');
            element.style.display = "";
            element.click();
            close();
          }}
        >
          Export as HTML
        </MenuItem>
        <MenuItem
          onClick={()=>{
            const blob = new Blob([workshop.figsToHTML()],
                                  {type: 'text/html;charset=utf8;'});
            const element = document.createElement('a');
            element.setAttribute("href", window.URL.createObjectURL(blob));
            element.setAttribute("download", 'figures.html');
            element.style.display = "";
            element.click();
            close();
          }}
        >
          Export Figures
        </MenuItem>
        <MenuItem key='filemenu-new' onClick={newWithWarning} >
          New
        </MenuItem>
      </Menu>

      {/* --------------------------------------------------------------------*/}

      <Dialog
        style={{height: 450}}
        open={pickerVisible}
        onClose={() => close()}
        key='picker-dialog'
      >
        <DialogTitle>Select File</DialogTitle>
        <DialogContent>
          {path.slice(0).reverse().map((folder, idx) => folder.name + (idx === path.length-1 ? '' : ':'))}
          <List
            style={{height: 175, border: '1px solid black', overflow: 'auto'}}
          >
            {path.length === 1 ? null :
             <ListItem button dense key='picker-dir-up'
                       onClick={() => {
                         list(path[1].id);
                         setPath(path.slice(1));
                       }}>
               <ListItemIcon><ArrowUpward /></ListItemIcon>
               <ListItemText>(Up ...)</ListItemText>
             </ListItem>}
            {docs.map((item, idx) => {
              return (
                <ListItem button dense key={item.id} disabled={fetching}
                          onClick={() => {
                            if (item.mimeType === 'application/json') {
                              console.log('Setting:', item);
                              workshop.setDoc({name: item.name, id: item.id});
                            }
                            else {
                              setPath([item, ...path]);
                              list(item.id);
                            }}}
                >
                  <ListItemIcon>
                    {item.mimeType === 'application/json' ? <Description /> : <Folder />}
                  </ListItemIcon>
                  <ListItemText>{item.name}</ListItemText>
                </ListItem>);
            })}
          </List>
          <div style={{display: 'flex', margin: '0.5em', alignItems: 'baseline',
                       justifyContent: 'space-between'}}>
            <TextField
              key='picker-dialog-textfield'
              style={{width: '50%'}}
              label='File name' variant='outlined'
              value={workshop.doc.name}
              onBlur={() => {
                console.log('Blurring');
                if (!workshop.doc.name.endsWith('.cws'))
                  workshop.setDocName(workshop.doc.name + '.cws');
              }}
              onChange={(e) => {
                console.log('OC', e.target.value);
                let newVal = e.target.value;
                if (action.startsWith('save'))
                  workshop.setDocName(newVal);
              }}/>
            <span>
              <Button
                color="primary" 
                disabled={!workshop.doc.name || fetching}
                onClick={()=>{
                  close();
                  if (action === 'save') {
                    save();
                  }
                  else if (action === 'save as') {
                    // note this approach will silently create a new file possibly
                    // having the same name as an existing file
                    workshop.doc.id = null; // need to null this out to create new file
                    save();
                  }
                  else if (action === 'load') {
                    load();
                  }
                }}>
                {action}
              </Button>
              <Button color="secondary" 
                      onClick={() => close()}>
                Cancel
              </Button>
            </span>
          </div>
        </DialogContent>
      </Dialog> 

      {/* --------------------------------------------------------------------*/}

      <Dialog
        open={warningVisible}
        onClose={() => close()}
        key='warning-dialog'
      >
        <DialogTitle>Unsaved Changes!</DialogTitle>
        <DialogContent>
          Do you want to save your unsaved changes?
          <DialogActions>
            <Button
              color="primary" variant="outlined"
              onClick={() => {
                close();
                doSave();
              }}>
              Save
              </Button>
            <Button color="secondary" variant="outlined"
                    onClick={() => {
                      close();
                      if (action === 'new') workshop.newClaimSet();
                      if (action === 'load') openPicker('load');
                    }}>
              Discard
            </Button>
          </DialogActions>
        </DialogContent>
      </Dialog> 



    </span>);
});


var picker = null;

// Create and render a Picker object for searching images.
export const createGooglePicker = () => {
  const create = () => {
    const google = window.google;
    const oauthToken = OAuthToken;
    console.log('Creating picker', pickerApiLoaded, oauthToken);
    if (pickerApiLoaded && oauthToken) {
      if (!picker) {
        //var view = new google.picker.View(google.picker.ViewId.DOCS);
        var view = new google.picker.DocsView().setIncludeFolders();
        //view.setMimeTypes("application/json");
        picker = new google.picker.PickerBuilder()
          .enableFeature(google.picker.Feature.NAV_HIDDEN)
          .enableFeature(google.picker.Feature.MULTISELECT_ENABLED)
          .setAppId(appId)
          .setOAuthToken(oauthToken)
          .addView(view)
          .setDeveloperKey(API_KEY)
          .setCallback((val) => console.log(val))
          .build();
      }
      picker.setVisible(true);
    }
  };
  authenticated(create);
}

